import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, User, Target, FileText, Calendar, Clock, Bot, Trophy } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import api from '../utils/api';

const searchCategories = [
  { type: 'page', label: 'Pages', icon: Search },
  { type: 'prospect', label: 'Prospects', icon: Target },
  { type: 'script', label: 'Scripts', icon: FileText },
];

const pageLinks = [
  { name: 'Dashboard', path: '/', icon: Search, keywords: ['home', 'activity', 'tracking', 'daily'] },
  { name: 'Prospect Pipeline', path: '/pipeline', icon: Target, keywords: ['leads', 'kanban', 'prospects', 'sales'] },
  { name: 'Appointments', path: '/appointments', icon: Calendar, keywords: ['meetings', 'schedule', 'followup'] },
  { name: 'Time Blocking', path: '/time-blocking', icon: Clock, keywords: ['calendar', 'schedule', 'planning'] },
  { name: 'AI Coach', path: '/ai-assistant', icon: Bot, keywords: ['chatgpt', 'ai', 'coaching', 'help', 'suggestions'] },
  { name: 'Content Planner', path: '/calendar', icon: Calendar, keywords: ['social media', 'posts', 'content'] },
  { name: 'Fast Start Planner', path: '/planner', icon: FileText, keywords: ['goals', 'sprint', 'district', 'fna'] },
  { name: 'Scripts & Playbook', path: '/playbook', icon: FileText, keywords: ['scripts', 'objections', 'cold call', 'warm market'] },
  { name: 'Leaderboard', path: '/leaderboard', icon: Trophy, keywords: ['ranking', 'badges', 'points', 'competition'] },
  { name: 'My Profile', path: '/profile', icon: User, keywords: ['account', 'settings', 'password'] },
];

export const UniversalSearch = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ pages: [], prospects: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    if (!isOpen) {
      setQuery('');
      setResults({ pages: [], prospects: [] });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!query.trim()) {
      setResults({ pages: [], prospects: [] });
      return;
    }

    const searchQuery = query.toLowerCase();
    
    // Search pages
    const matchedPages = pageLinks.filter(page => 
      page.name.toLowerCase().includes(searchQuery) ||
      page.keywords.some(kw => kw.includes(searchQuery))
    );

    setResults(prev => ({ ...prev, pages: matchedPages }));

    // Search prospects (debounced)
    const timer = setTimeout(async () => {
      if (query.length >= 2) {
        setLoading(true);
        try {
          const response = await api.get('/prospects');
          const matchedProspects = response.data.filter(p =>
            p.name?.toLowerCase().includes(searchQuery) ||
            p.phone?.includes(query) ||
            p.message?.toLowerCase().includes(searchQuery)
          ).slice(0, 5);
          setResults(prev => ({ ...prev, prospects: matchedProspects }));
        } catch (error) {
          // Silent fail - search continues with other results
        } finally {
          setLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (path) => {
    navigate(path);
    onClose();
  };

  const handleProspectSelect = (prospect) => {
    navigate(`/pipeline?highlight=${prospect.id}`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0">
        <div className="flex items-center border-b px-4">
          <Search className="h-5 w-5 text-gray-400" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, prospects, scripts..."
            className="border-0 focus-visible:ring-0 text-lg py-6"
            data-testid="universal-search-input"
          />
          {query && (
            <button onClick={() => setQuery('')} className="p-1 hover:bg-gray-100 rounded">
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {!query && (
            <div className="p-4 text-center text-gray-500">
              <p className="text-sm">Type to search pages, prospects, and more...</p>
              <div className="mt-3 flex justify-center gap-2">
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">⌘</kbd>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">K</kbd>
                <span className="text-xs">to open search anytime</span>
              </div>
            </div>
          )}

          {/* Pages Results */}
          {results.pages.length > 0 && (
            <div className="p-2">
              <p className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">Pages</p>
              {results.pages.map((page) => (
                <button
                  key={page.path}
                  onClick={() => handleSelect(page.path)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors text-left"
                  data-testid={`search-result-${page.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <page.icon className="h-5 w-5 text-[#E31837]" />
                  <span className="font-medium">{page.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Prospects Results */}
          {results.prospects.length > 0 && (
            <div className="p-2 border-t">
              <p className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">Prospects</p>
              {results.prospects.map((prospect) => (
                <button
                  key={prospect.id}
                  onClick={() => handleProspectSelect(prospect)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors text-left"
                >
                  <Target className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="font-medium">{prospect.name}</p>
                    <p className="text-xs text-gray-500">{prospect.platform} • {prospect.stage || 'New Lead'}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {query && results.pages.length === 0 && results.prospects.length === 0 && !loading && (
            <div className="p-8 text-center text-gray-500">
              <Search className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No results found for "{query}"</p>
            </div>
          )}

          {loading && (
            <div className="p-4 text-center text-gray-500">
              <p className="text-sm">Searching...</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="border-t p-2 flex items-center justify-between text-xs text-gray-500">
          <div className="flex gap-4">
            <span><kbd className="px-1 bg-gray-100 rounded">↵</kbd> to select</span>
            <span><kbd className="px-1 bg-gray-100 rounded">esc</kbd> to close</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
